/**
 * The severity filter the Alerts queue and Detections stream share.
 *
 * At `sm` and up it is the portal's pill rail: All · Critical · High ·
 * Medium · Low · Informational in one rounded bar. Below `sm` that rail is
 * wider than the screen — it was the row that forced the whole page to
 * scroll sideways on a phone (Steve, 2026-09-21) — so it becomes a select,
 * placed first in the same stack as the Status, Disposition and Source
 * selects. One component renders both shapes and the screens choose
 * where each goes; the two lists used to carry a copy each.
 */

import { SEVERITY_COLORS } from '@bluesphere/ui-kit';

export const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Informational'] as const;
export const SEVERITY_FILTERS = ['All', ...SEVERITIES] as const;
export type SeverityFilterValue = (typeof SEVERITY_FILTERS)[number];

/** The filter-row select, on the gradient canvas. Shared by every list filter. */
export const filterSelectClass =
  'w-full rounded-input border border-white/20 bg-white/10 px-3 py-1.5 text-sm text-white sm:w-auto';

export function isSeverityFilter(value: string): value is SeverityFilterValue {
  return (SEVERITY_FILTERS as readonly string[]).includes(value);
}

interface Props {
  value: SeverityFilterValue;
  onChange: (value: SeverityFilterValue) => void;
}

/** Phone shape: a select in the filter stack. Hidden at `sm` and up. */
export function SeveritySelect({ value, onChange }: Props) {
  return (
    <select
      aria-label="Severity"
      value={value}
      onChange={(e) => {
        if (isSeverityFilter(e.target.value)) onChange(e.target.value);
      }}
      className={`${filterSelectClass} sm:hidden`}
    >
      {SEVERITY_FILTERS.map((option) => (
        <option key={option} value={option} className="text-bluesphere-textOnLight">
          {option === 'All' ? 'All severities' : option}
        </option>
      ))}
    </select>
  );
}

/** Desktop shape: the pill rail. Hidden below `sm`. */
export function SeverityPills({ value, onChange }: Props) {
  return (
    <div
      className="bs-scroll hidden gap-1 overflow-x-auto rounded-pill bg-white/10 p-1.5 sm:flex"
      role="group"
      aria-label="Severity"
    >
      {SEVERITY_FILTERS.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={value === option}
          className={
            'flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ' +
            (value === option
              ? 'bg-white text-bluesphere-primary'
              : 'text-white/80 hover:bg-white/10')
          }
        >
          {option !== 'All' && (
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: SEVERITY_COLORS[option] }}
            />
          )}
          {option}
        </button>
      ))}
    </div>
  );
}
